import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trainingCampAPI } from '../services/api';
import {
  CheckCircle,
  Clock,
  Calendar,
  AlertCircle,
  Video,
  FileText,
  User,
  Phone,
  Save,
  Send,
  Lock,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Info,
  Droplet,
  Instagram
} from 'lucide-react';
import './TrainingCampOnboarding.css';

/** Helper to parse time strings like '11:00 AM', '01:30 PM', '11:00' into minutes since midnight for strict chronological sorting */
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const str = String(timeStr).trim();
  const match = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return 0;
  let h = parseInt(match[1], 10);
  const min = parseInt(match[2], 10);
  const period = match[3] ? match[3].toUpperCase() : null;
  if (period === 'PM' && h < 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h * 60 + min;
};

/** Extract meeting link from text or URL */
const getMeetingUrl = (text) => {
  if (!text) return null;
  const trimmed = text.trim();
  const match = trimmed.match(/(https?:\/\/[^\s]+)/i);
  if (match) return match[0];
  if (
    trimmed.startsWith('meet.google.com') ||
    trimmed.startsWith('zoom.us') ||
    trimmed.startsWith('teams.microsoft.com') ||
    trimmed.startsWith('instagram.com') ||
    trimmed.startsWith('www.instagram.com') ||
    trimmed.startsWith('chat.whatsapp.com')
  ) {
    return `https://${trimmed}`;
  }
  return null;
};

const normalizeSlot = (s) => {
  if (!s) return null;
  return {
    ...s,
    startTime: s.startTime || s.start_time || '',
    start_time: s.startTime || s.start_time || '',
    endTime: s.endTime || s.end_time || '',
    end_time: s.endTime || s.end_time || '',
    notes: s.notes || ''
  };
};

const TrainingCampOnboarding = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Screen states
  const [loading, setLoading] = useState(true);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [activeStep, setActiveStep] = useState(1);

  // Step 1 Form
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    gender: '',
    dob: '',
    blood_group: '',
    batch_id: '',
    batch_date_label: '',
    video_intro_url: '',
    why_join_url: '',
  });

  const [savingDraft, setSavingDraft] = useState(false);
  const [submittingStep1, setSubmittingStep1] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Step 2 Slots
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [bookingSlot, setBookingSlot] = useState(false);
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [bookedSlot, setBookedSlot] = useState(null);

  const handlePrevMonth = () => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // 1. Initial Load & Auth Check
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    loadStatus();
  }, [isAuthenticated, authLoading]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setFormError('');
      const res = await trainingCampAPI.getMyStatus();
      if (res.data?.success) {
        const data = res.data.data;
        setEligibilityData(data);
        const sub = data.submission;
        const prefill = data.prefill || {};
        const reg = data.registration;
        const regBatches = data.registeredBatches || data.batches || [];
        const defaultBatch = regBatches[0] || null;

        setSubmission(sub);

        // Pre-fill prioritizing saved submission, then registration data, then user profile
        setFormData({
          name: sub?.name || prefill.name || reg?.name || user?.name || '',
          phone: sub?.phone || prefill.phone || reg?.phone || (user?.phone ? String(user.phone).replace(/\D/g, '').slice(-10) : ''),
          gender: sub?.gender || prefill.gender || reg?.gender || '',
          dob: (sub?.dob || prefill.dob || reg?.dob) ? String(sub?.dob || prefill.dob || reg?.dob).split('T')[0] : '',
          blood_group: sub?.bloodGroup || sub?.blood_group || prefill.blood_group || '',
          batch_id: sub?.batchId || sub?.batch_id || prefill.batch_id || (defaultBatch ? String(defaultBatch.id) : ''),
          batch_date_label: sub?.batchDateLabel || sub?.batch_date_label || prefill.batch_date_label || defaultBatch?.date_label || '',
          video_intro_url: sub?.videoIntroUrl || sub?.video_intro_url || '',
          why_join_url: sub?.whyJoinUrl || sub?.why_join_url || '',
        });

        const isStep1Done = Boolean(sub?.is_step1_submitted || sub?.isStep1Submitted);
        if (isStep1Done) {
          setActiveStep(2);
        } else {
          setActiveStep(1);
        }

        if (sub?.slot) {
          setBookedSlot(normalizeSlot(sub.slot));
        }

        // If Step 1 already submitted, load slots
        if (isStep1Done) {
          fetchSlots(sub.batch_id || sub.batchId || defaultBatch?.id);
        }
      }
    } catch (err) {
      console.error('Failed to load training camp status:', err);
      if (err.response?.status === 401) {
        setFormError('Please login to access the onboarding portal.');
      } else {
        setFormError(err.response?.data?.message || 'Failed to load your registration status. Please refresh.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async (batchId) => {
    try {
      setSlotsLoading(true);
      const res = await trainingCampAPI.getAvailableInterviewSlots(batchId || formData.batch_id);
      if (res.data?.success) {
        const raw = res.data.data?.slots || res.data.slots || (Array.isArray(res.data.data) ? res.data.data : []);
        const slots = raw.map(s => ({
          ...s,
          start_time: s.start_time || s.startTime,
          end_time: s.end_time || s.endTime,
          available_spots: s.available_spots !== undefined ? s.available_spots : (s.availableSpots !== undefined ? s.availableSpots : Math.max(0, (s.capacity || 1) - (s.bookedCount || s.booked_count || 0))),
          booked_count: s.booked_count !== undefined ? s.booked_count : (s.bookedCount || 0)
        }));

        slots.sort((a, b) => {
          if (a.date !== b.date) return String(a.date).localeCompare(String(b.date));
          return parseTimeToMinutes(a.start_time) - parseTimeToMinutes(b.start_time);
        });

        setAvailableSlots(slots);
        if (slots.length > 0) {
          const firstDate = slots[0].date;
          setSelectedDate((prev) => (prev && slots.some((s) => s.date === prev) ? prev : firstDate));
          if (firstDate) {
            const [y, m, d] = firstDate.split('-');
            setCalendarMonth(new Date(Number(y), Number(m) - 1, 1));
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch interview slots:', err);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'batch_id') {
        const batchList = eligibilityData?.registeredBatches?.length
          ? eligibilityData.registeredBatches
          : (eligibilityData?.batches || []);
        const selectedBatch = batchList.find(b => String(b.id) === String(value));
        if (selectedBatch) {
          updated.batch_date_label = selectedBatch.date_label;
        }
      }
      return updated;
    });
    setFormError('');
  };

  // Strict mobile number input handler (numbers only, max 10 digits)
  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, phone: digitsOnly }));
    setFormError('');
  };

  // Save Step 1 Draft
  const handleSaveDraft = async () => {
    try {
      setSavingDraft(true);
      setFormError('');
      setSuccessMessage('');

      const res = await trainingCampAPI.saveStep1Draft(formData);
      if (res.data?.success) {
        setSubmission(res.data.data);
        setSuccessMessage('Progress saved as draft! You can safely return and complete this anytime.');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save progress. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  // Submit Step 1
  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');

    // Strict Validations
    if (!formData.name?.trim()) return setFormError('Please enter your full name.');
    if (!formData.phone || formData.phone.length !== 10 || !/^[6-9]\d{9}$/.test(formData.phone)) {
      return setFormError('Please enter a valid 10-digit mobile number.');
    }
    if (!formData.gender) return setFormError('Please select your gender.');
    if (!formData.dob) return setFormError('Please provide your date of birth.');
    if (!formData.blood_group) return setFormError('Please select your blood group.');
    if (!formData.batch_id) return setFormError('Training camp batch date is required.');
    if (!formData.video_intro_url?.trim()) return setFormError('Please provide your Google Drive link for the Video Introduction.');
    if (!formData.why_join_url?.trim()) return setFormError('Please provide your Google Drive link for "Why you want to join Invincible?".');

    // Google Drive URL check
    if (!formData.video_intro_url.includes('http') || !formData.why_join_url.includes('http')) {
      return setFormError('Please ensure both video links are valid URLs starting with http:// or https://');
    }

    try {
      setSubmittingStep1(true);
      const res = await trainingCampAPI.submitStep1(formData);
      if (res.data?.success) {
        setSubmission(res.data.data);
        setActiveStep(2);
        setSuccessMessage('Step 1 submitted successfully! Please schedule your interview slot below.');
        fetchSlots(formData.batch_id);
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to submit Step 1. Please try again.');
    } finally {
      setSubmittingStep1(false);
    }
  };

  // Step 2: Book Interview Slot
  const handleBookSlot = async () => {
    if (!selectedSlotId) {
      setFormError('Please select a time slot first.');
      return;
    }

    try {
      setBookingSlot(true);
      setFormError('');
      setSuccessMessage('');

      let res;
      if (bookedSlot && rescheduleMode) {
        res = await trainingCampAPI.rescheduleInterviewSlot(selectedSlotId);
      } else {
        res = await trainingCampAPI.bookInterviewSlot(selectedSlotId);
      }

      if (res.data?.success) {
        setSubmission(res.data.data);
        setBookedSlot(normalizeSlot(res.data.data?.slot));
        setRescheduleMode(false);
        setSuccessMessage('Interview slot booked successfully! Our team will meet you at the scheduled time.');
        fetchSlots(submission?.batch_id || formData.batch_id);
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to book slot. It might be fully booked. Please choose another.');
    } finally {
      setBookingSlot(false);
    }
  };

  // Render: Unauthenticated
  if (!isAuthenticated && !authLoading) {
    return (
      <div className="tc-onboarding-container">
        <div className="tc-onboarding-wrapper">
          <div className="tc-card tc-restricted-card">
            <div className="tc-restricted-header">
              <div className="tc-3d-emoji-badge" aria-hidden="true">
                <span className="tc-3d-emoji">🔐</span>
              </div>
              <div className="tc-restricted-header-text">
                <h2 className="tc-restricted-title">Volunteer Login Required</h2>
                <p className="tc-restricted-subtitle">
                  You must be logged in to access the Instructor's Skill Development &amp; Survival Camp onboarding form.
                </p>
              </div>
            </div>
            <div className="tc-restricted-actions">
              <button
                type="button"
                className="tc-btn tc-btn-primary"
                onClick={() => navigate('/login', { state: { from: '/training-camp/onboarding' } })}
              >
                Login with Mobile OTP
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render: Loading
  if (loading || authLoading) {
    return (
      <div className="tc-onboarding-container">
        <div className="tc-onboarding-wrapper">
          <div className="tc-card tc-loading-card">
            <div className="tc-spinner"></div>
            <p>Verifying your Training Camp registration status...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render: Not Eligible (Not registered for an upcoming Camp 35 batch)
  if (eligibilityData && !eligibilityData.isEligible) {
    return (
      <div className="tc-onboarding-container">
        <div className="tc-onboarding-wrapper">
          <div className="tc-card tc-restricted-card">
            {/* Header with 3D Apple Emoji Icon & Titles */}
            <div className="tc-restricted-header">
              <div className="tc-3d-emoji-badge" aria-hidden="true">
                <span className="tc-3d-emoji">⚠️</span>
              </div>
              <div className="tc-restricted-header-text">
                <h2 className="tc-restricted-title">Confirmed Training Camp Registration Required</h2>
                <p className="tc-restricted-subtitle">
                  This onboarding link is exclusively for candidates with a <strong>confirmed registration</strong> for an upcoming batch of the{' '}
                  <strong>Instructor's Skill Development &amp; Survival Camp</strong> (Basic Training Camp).
                </p>
              </div>
            </div>

            {/* Notice Callout Box with Aligned 3D Icon */}
            <div className="tc-notice-box tc-notice-warning">
              <div className="tc-notice-icon-wrap" aria-hidden="true">
                <span className="tc-notice-emoji">💡</span>
              </div>
              <div className="tc-notice-content">
                <h4 className="tc-notice-title">Registration not confirmed or haven't registered yet?</h4>
                <p className="tc-notice-desc">
                  Please ensure your registration is confirmed for an upcoming training camp batch to access the volunteer introduction and interview booking.
                </p>
                <p className="tc-notice-subdesc">
                  Already have a confirmed registration with another mobile number? Please log in with your registered phone number.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="tc-restricted-actions">
              <a
                href="/events/instructors-skill-development-survival-camp-saputara"
                className="tc-btn tc-btn-primary"
                style={{ textDecoration: 'none' }}
              >
                Register for Training Camp
              </a>
              <button
                type="button"
                className="tc-btn tc-btn-secondary"
                onClick={() => navigate('/login', { state: { from: '/training-camp/onboarding' } })}
              >
                Login with Registered Phone
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const registeredBatches = eligibilityData?.registeredBatches || [];
  const hasMultipleBatches = registeredBatches.length > 1;
  const isStep1Done = Boolean(submission?.is_step1_submitted || submission?.isStep1Submitted);

  // Group slots by date for Cal.com view
  const slotsByDate = availableSlots.reduce((acc, slot) => {
    const d = slot.date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(slot);
    return acc;
  }, {});

  // Sort slots within each date strictly early-to-late
  Object.keys(slotsByDate).forEach((d) => {
    slotsByDate[d].sort((a, b) => parseTimeToMinutes(a.start_time) - parseTimeToMinutes(b.start_time));
  });

  const datesList = Object.keys(slotsByDate).sort();
  const currentDaySlots = selectedDate ? (slotsByDate[selectedDate] || []) : [];

  const matchedSlotFromList = bookedSlot?.id ? availableSlots.find((s) => s.id === bookedSlot.id) : null;
  const activeBookedSlot = bookedSlot ? {
    ...bookedSlot,
    startTime: bookedSlot.startTime || bookedSlot.start_time || matchedSlotFromList?.start_time || '',
    start_time: bookedSlot.startTime || bookedSlot.start_time || matchedSlotFromList?.start_time || '',
    endTime: bookedSlot.endTime || bookedSlot.end_time || matchedSlotFromList?.end_time || '',
    end_time: bookedSlot.endTime || bookedSlot.end_time || matchedSlotFromList?.end_time || '',
    notes: bookedSlot.notes || matchedSlotFromList?.notes || ''
  } : null;

  const candidateName = formData.name || submission?.name || user?.name || user?.firstName || 'Candidate';

  const WEEKDAYS_CAL = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const calYear = calendarMonth.getFullYear();
  const calMonthIdx = calendarMonth.getMonth();
  const daysInMonth = new Date(calYear, calMonthIdx + 1, 0).getDate();
  const firstDayOfWeek = new Date(calYear, calMonthIdx, 1).getDay();
  const monthTitle = calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="tc-onboarding-container">
      <div className="tc-onboarding-wrapper">
        {/* Stepper Progress */}
        <div className="tc-stepper">
          <div
            className={`tc-step-item ${activeStep === 1 ? 'active' : ''} ${
              isStep1Done ? 'completed' : ''
            }`}
            onClick={() => setActiveStep(1)}
            style={{ cursor: 'pointer' }}
          >
            <div className="tc-step-badge">
              {isStep1Done ? <CheckCircle size={16} /> : '1'}
            </div>
            <span>Basic Details & Video</span>
          </div>

          <div className={`tc-step-line ${isStep1Done ? 'completed' : ''}`}></div>

          <div
            className={`tc-step-item ${activeStep === 2 ? 'active' : ''} ${
              bookedSlot ? 'completed' : ''
            }`}
            onClick={() => {
              if (isStep1Done) {
                setActiveStep(2);
                if (submission?.batch_id || formData.batch_id) {
                  fetchSlots(submission?.batch_id || formData.batch_id);
                }
              }
            }}
            style={{ cursor: isStep1Done ? 'pointer' : 'not-allowed' }}
          >
            <div className="tc-step-badge">
              {bookedSlot ? <CheckCircle size={16} /> : '2'}
            </div>
            <span>Interview Schedule</span>
          </div>
        </div>

        {/* Global Notifications */}
        {formError && (
          <div className="tc-alert tc-alert-error">
            <AlertCircle size={20} />
            <span>{formError}</span>
          </div>
        )}
        {successMessage && (
          <div className="tc-alert tc-alert-success">
            <CheckCircle size={20} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* ================= STEP 1: BASIC DETAILS & VIDEO ================= */}
        {activeStep === 1 && (
          <div className="tc-card">
            {/* Header with Camp Badge */}
            <div className="tc-welcome-header">
              <span className="tc-camp-tag">
                Instructor's Skill Development & Survival Camp
              </span>
              <h1 className="tc-title">Volunteer Candidate Onboarding</h1>
              <div className="tc-welcome-message-box">
                <p className="tc-welcome-lead">Jai Hind!</p>
                <p>
                  We are excited to welcome the young team of volunteers at Invincible. This link is for those, who have applied for the Instructor's Skill Development &amp; Survival Camp, also known as Basic Training Camp. If not then you can visit the website and apply for the same.
                </p>
                <p>
                  The second step in the volunteer's selection is the submission of an introduction. Please watch the video to get more information.
                </p>
                <p>
                  On successful submission of this form, you will need to place an appointment slot for an online video interview.
                </p>
              </div>
            </div>

            {/* YouTube Video Embed - 16:9 full frame */}
            <div className="tc-video-container">
              <iframe
                src="https://www.youtube.com/embed/qQdHjXcH37Y?rel=0"
                title="Invincible Training Camp Briefing"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>

            {/* Locked Notice if already submitted */}
            {isStep1Done && (
              <div className="tc-notice-box tc-notice-locked" style={{ marginBottom: '24px' }}>
                <Lock size={20} />
                <div style={{ flex: 1 }}>
                  <strong>Step 1 has already been submitted and locked.</strong>
                  <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>
                    Your details and video links are securely recorded. Click below or use the stepper to schedule or view your interview slot.
                  </p>
                </div>
                <button
                  type="button"
                  className="tc-btn-small"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={() => {
                    setActiveStep(2);
                    if (submission?.batch_id || formData.batch_id) {
                      fetchSlots(submission?.batch_id || formData.batch_id);
                    }
                  }}
                >
                  Go to Step 2 <ChevronRight size={16} />
                </button>
              </div>
            )}

            {/* Step 1 Form */}
            <form onSubmit={handleSubmitStep1} className="tc-form">
              <div className="tc-form-grid">
                {/* Full Name */}
                <div className="tc-form-group">
                  <label htmlFor="name">
                    <User size={16} /> Full Name <span className="tc-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    disabled={isStep1Done}
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="tc-input"
                    required
                  />
                </div>

                {/* Mobile No */}
                <div className="tc-form-group">
                  <label htmlFor="phone">
                    <Phone size={16} /> Mobile Number <span className="tc-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    maxLength={10}
                    disabled={isStep1Done}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="10-digit mobile number"
                    className="tc-input"
                    required
                  />
                </div>

                {/* Gender */}
                <div className="tc-form-group">
                  <label htmlFor="gender">
                    Gender <span className="tc-required">*</span>
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    disabled={isStep1Done}
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="tc-select"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* DOB */}
                <div className="tc-form-group">
                  <label htmlFor="dob">
                    <Calendar size={16} /> Date of Birth <span className="tc-required">*</span>
                  </label>
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    disabled={isStep1Done}
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="tc-input"
                    required
                  />
                </div>

                {/* Training Camp Batch Selection (Dynamic: Non-selective if 1 batch registered; Dropdown if 2+) */}
                <div className="tc-form-group tc-form-group-full">
                  <label htmlFor="batch_id">
                    <Calendar size={16} /> Training Camp Batch Date <span className="tc-required">*</span>
                  </label>

                  {hasMultipleBatches ? (
                    <select
                      id="batch_id"
                      name="batch_id"
                      disabled={isStep1Done}
                      value={formData.batch_id}
                      onChange={handleInputChange}
                      className="tc-select"
                      required
                    >
                      <option value="">Select your Registered Camp Date</option>
                      {registeredBatches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.date_label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="tc-registered-batch-card">
                      <div className="tc-batch-date-display">
                        <Calendar size={18} className="tc-icon-orange" />
                        <span className="tc-batch-date-text">
                          {formData.batch_date_label || registeredBatches[0]?.date_label}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Field: Blood Group (Placed before Video Introduction Link) */}
                <div className="tc-form-group tc-form-group-full">
                  <label htmlFor="blood_group">
                    <Droplet size={16} /> Blood Group <span className="tc-required">*</span>
                  </label>
                  <select
                    id="blood_group"
                    name="blood_group"
                    disabled={isStep1Done}
                    value={formData.blood_group}
                    onChange={handleInputChange}
                    className="tc-select"
                    required
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="I don't know">I don't know</option>
                  </select>
                </div>

              </div>

              {/* Common Google Drive Links Section */}
              <div className="tc-drive-section">
                <div className="tc-drive-section-header">
                  <div className="tc-drive-title">
                    <Video size={20} style={{ color: '#de5824' }} />
                    <h3>Google Drive Links</h3>
                  </div>
                  <p className="tc-drive-subtitle">
                    Upload your vertical video recordings (30–60 sec) to Google Drive and paste the shareable links below.
                  </p>
                </div>

                <div className="tc-form-grid">
                  {/* Video Introduction */}
                  <div className="tc-form-group">
                    <label htmlFor="video_intro_url">
                      Video Introduction <span className="tc-required">*</span>
                    </label>
                    <input
                      type="url"
                      id="video_intro_url"
                      name="video_intro_url"
                      disabled={isStep1Done}
                      value={formData.video_intro_url}
                      onChange={handleInputChange}
                      placeholder="https://drive.google.com/file/d/..."
                      className="tc-input"
                      required
                    />
                  </div>

                  {/* Why you want to join Invincible */}
                  <div className="tc-form-group">
                    <label htmlFor="why_join_url">
                      Why you want to join Invincible? <span className="tc-required">*</span>
                    </label>
                    <input
                      type="url"
                      id="why_join_url"
                      name="why_join_url"
                      disabled={isStep1Done}
                      value={formData.why_join_url}
                      onChange={handleInputChange}
                      placeholder="https://drive.google.com/file/d/..."
                      className="tc-input"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Important Instructions Box */}
              <div className="tc-video-guidelines-box">
                <div className="tc-guidelines-header">
                  <Info size={20} className="tc-icon-amber" />
                  <h4>Important Instructions for Video Links:</h4>
                </div>
                <ul className="tc-guidelines-list">
                  <li>
                    <strong>Vertical Video:</strong> The video must be recorded in portrait mode (vertical orientation).
                  </li>
                  <li>
                    <strong>Length:</strong> Keep the video strictly between <strong>30 seconds to 60 seconds</strong>.
                  </li>
                  <li>
                    <strong>Share Permissions:</strong> Don't forget to set Google Drive link access to{' '}
                    <span className="tc-highlight">"Anyone with the link can view"</span> so our interview panel can review it.
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              {isStep1Done ? (
                <div className="tc-form-actions" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveStep(2);
                      if (submission?.batch_id || formData.batch_id) {
                        fetchSlots(submission?.batch_id || formData.batch_id);
                      }
                    }}
                    className="tc-btn tc-btn-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px' }}
                  >
                    <span>Proceed to Step 2 (Interview Schedule)</span>
                    <ChevronRight size={18} />
                  </button>
                </div>
              ) : (
                <div className="tc-form-actions">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={savingDraft || submittingStep1}
                    className="tc-btn tc-btn-secondary"
                  >
                    <Save size={18} />
                    {savingDraft ? 'Saving Draft...' : 'Save Progress'}
                  </button>

                  <button
                    type="submit"
                    disabled={submittingStep1 || savingDraft}
                    className="tc-btn tc-btn-primary"
                  >
                    <Send size={18} />
                    {submittingStep1 ? 'Submitting...' : 'Submit Step 1 & Proceed'}
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {/* ================= STEP 2: INTERVIEW SCHEDULING ================= */}
        {activeStep === 2 && (
          <div className="tc-card">
            {/* Top Back to Step 1 Button */}
            <div style={{ marginBottom: '16px' }}>
              <button
                type="button"
                className="tc-btn tc-btn-secondary"
                onClick={() => setActiveStep(1)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <ChevronLeft size={16} /> Back to Step 1
              </button>
            </div>

            <div className="tc-welcome-header">
              <span className="tc-camp-tag">Step 2 of 2</span>
              <h1 className="tc-title">Schedule Your Volunteer Interview</h1>
              <p className="tc-subtitle">
                Select your preferred date and time slot from the available options. Each slot has limited seats.
              </p>
            </div>

            {/* If Slot is already booked and NOT in reschedule mode */}
            {activeBookedSlot && !rescheduleMode ? (
              <div className="tc-booked-confirmation">
                <div className="tc-conf-hero">
                  <div className="tc-conf-hero-badge">
                    <span className="tc-3d-emoji-hero" role="img" aria-label="Celebration">🎉</span>
                  </div>
                  <div className="tc-conf-hero-body">
                    <div className="tc-conf-status-tag">
                      <span className="tc-conf-pulse-dot" />
                      <span>Volunteer Interview Confirmed</span>
                    </div>
                    <h3 className="tc-conf-hero-title">Volunteer Interview Slot Confirmed! 🎉</h3>
                    <div className="tc-conf-greeting">
                      Jai Hind, <span className="tc-conf-greeting-name">{candidateName}</span>!
                    </div>
                    <p className="tc-conf-hero-subtitle">
                      Your onboarding interview for Training Camp has been successfully scheduled.
                    </p>
                  </div>
                </div>

                <div className="tc-conf-details-card">
                  {/* Date */}
                  <div className="tc-conf-row">
                    <div className="tc-conf-emoji-box date-box">
                      <span className="tc-3d-emoji-row" role="img" aria-label="Calendar">📅</span>
                    </div>
                    <div className="tc-conf-row-content">
                      <span className="tc-conf-label">Date:</span>
                      <div className="tc-conf-val-highlight date-highlight">
                        {activeBookedSlot.date ? (
                          new Date(activeBookedSlot.date + 'T00:00:00').toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        ) : 'Date Pending'}
                      </div>
                    </div>
                  </div>

                  {/* Time Slot */}
                  <div className="tc-conf-row">
                    <div className="tc-conf-emoji-box time-box">
                      <span className="tc-3d-emoji-row" role="img" aria-label="Clock">🕒</span>
                    </div>
                    <div className="tc-conf-row-content">
                      <span className="tc-conf-label">Time Slot:</span>
                      <div className="tc-conf-val-highlight time-highlight">
                        <span className="tc-conf-time-badge">
                          {activeBookedSlot.start_time || activeBookedSlot.startTime} – {activeBookedSlot.end_time || activeBookedSlot.endTime}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Interview Instructions Card */}
                  <div className="tc-conf-instructions-card">
                    <div className="tc-conf-instructions-header">
                      <div className="tc-conf-emoji-box phone-box">
                        <span className="tc-3d-emoji-row" role="img" aria-label="Mobile">📱</span>
                      </div>
                      <div className="tc-conf-instructions-title-group">
                        <span className="tc-conf-label">Interview Instructions</span>
                        <h4 className="tc-conf-instructions-heading">Conducted via Instagram</h4>
                      </div>
                    </div>

                    <p className="tc-conf-instructions-body">
                      The interview will take place on Instagram. Please visit the profile below at your scheduled time and either <strong className="tc-conf-highlight-action">DM or initiate a video call</strong> to connect for your interview.
                    </p>

                    {/* Instagram Profile Follow Link */}
                    {(() => {
                      const instagramProfileUrl = getMeetingUrl(activeBookedSlot.notes) || 'https://www.instagram.com/rushirajmori';

                      return (
                        <div className="tc-conf-insta-block">
                          <div className="tc-conf-insta-label-row">
                            <span className="tc-3d-emoji-xs" role="img" aria-label="Link">🔗</span>
                            <span className="tc-conf-insta-label">Instagram Profile:</span>
                          </div>
                          <div className="tc-conf-meeting-actions">
                            <a
                              href={instagramProfileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tc-conf-follow-link tc-conf-insta-link"
                              title="Open Instagram profile"
                            >
                              <Instagram size={16} className="tc-insta-icon" />
                              <span className="tc-conf-link-text">{instagramProfileUrl}</span>
                              <ExternalLink size={14} className="tc-conf-link-arrow" />
                            </a>
                            <a
                              href={instagramProfileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tc-conf-join-btn tc-conf-insta-join"
                            >
                              <Instagram size={15} />
                              <span>Open Instagram Profile</span>
                              <ExternalLink size={13} />
                            </a>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Punctuality Notice & Team Invincible Sign-off */}
                  <div className="tc-conf-signoff-card">
                    <div className="tc-conf-signoff-top">
                      <span className="tc-3d-emoji-tip" role="img" aria-label="Punctual">💡</span>
                      <div className="tc-conf-signoff-text">
                        <p className="tc-conf-punctual-line">
                          Please be punctual and ensure you are ready at your scheduled time.
                        </p>
                        <p className="tc-conf-punctual-line">
                          Please ensure you are in a quiet environment with a clean, distraction free background and proper lighting so we can have a smooth and comfortable conversation.
                        </p>
                        <p className="tc-conf-lookforward-line">
                          We look forward to connecting with you!
                        </p>
                      </div>
                    </div>
                    <div className="tc-conf-team-row">
                      <span className="tc-conf-team-tag">Team Invincible</span>
                    </div>
                  </div>
                </div>

                <div className="tc-conf-actions">
                  <button
                    type="button"
                    className="tc-btn tc-btn-secondary"
                    onClick={() => setActiveStep(1)}
                  >
                    <ChevronLeft size={16} /> View Step 1 Details
                  </button>
                  <button
                    type="button"
                    className="tc-btn tc-btn-outline tc-btn-reschedule"
                    onClick={() => {
                      setRescheduleMode(true);
                      fetchSlots(submission?.batch_id || formData.batch_id);
                    }}
                  >
                    <RefreshCw size={16} /> Reschedule Slot
                  </button>
                </div>
              </div>
            ) : (
              /* Calendar / Slot Picker (Cal.com style) */
              <div className="tc-cal-wrapper">
                {rescheduleMode && (
                  <div className="tc-notice-box tc-notice-warning" style={{ marginBottom: 20 }}>
                    <Info size={20} />
                    <div>
                      <strong>Rescheduling Interview:</strong> You currently have a slot booked on{' '}
                      <strong>{bookedSlot?.date} ({bookedSlot?.start_time} - {bookedSlot?.end_time})</strong>.
                      Select a new slot below to update.
                    </div>
                    <button
                      type="button"
                      className="tc-btn-small"
                      onClick={() => setRescheduleMode(false)}
                    >
                      Cancel Reschedule
                    </button>
                  </div>
                )}

                {slotsLoading ? (
                  <div className="tc-slots-loading">
                    <div className="tc-spinner"></div>
                    <p>Loading available slots...</p>
                  </div>
                ) : datesList.length === 0 ? (
                  <div className="tc-no-slots-box">
                    <div className="tc-restricted-header" style={{ marginBottom: 16 }}>
                      <div className="tc-3d-emoji-badge" aria-hidden="true">
                        <span className="tc-3d-emoji">🗓️</span>
                      </div>
                      <div className="tc-restricted-header-text">
                        <h3 className="tc-restricted-title" style={{ fontSize: '1.25rem', marginBottom: 4 }}>
                          No Interview Slots Available Currently
                        </h3>
                        <p className="tc-restricted-subtitle" style={{ fontSize: '0.9rem' }}>
                          Interview spots for your registered training camp batch ({formData.batch_date_label || registeredBatches[0]?.date_label || "Instructor's Skill Development & Survival Camp"}) have not been published yet, or all current slots have been booked.
                        </p>
                      </div>
                    </div>

                    <div className="tc-notice-box tc-notice-warning" style={{ margin: '16px 0 20px' }}>
                      <div className="tc-notice-icon-wrap" aria-hidden="true">
                        <span className="tc-notice-emoji">💡</span>
                      </div>
                      <div className="tc-notice-content">
                        <h4 className="tc-notice-title">How to book your interview slot?</h4>
                        <p className="tc-notice-desc">
                          The Invincible team periodically adds new interview batches. Please check back shortly or check the official WhatsApp group for slot release updates.
                        </p>
                        <p className="tc-notice-subdesc" style={{ marginTop: 4, color: '#64748b' }}>
                          Your Step 1 details and vertical video links are safely saved and ready for interview scheduling.
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        className="tc-btn tc-btn-secondary"
                        onClick={() => setActiveStep(1)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <ChevronLeft size={16} /> Back to Step 1
                      </button>

                      <button
                        type="button"
                        className="tc-btn tc-btn-outline"
                        onClick={() => fetchSlots(formData.batch_id || submission?.batch_id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <RefreshCw size={16} /> Refresh Available Slots
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="tc-cal-booking-container">
                    <div className="tc-cal-two-column-card">
                      {/* Left Column: Interactive Month Calendar (Image 2 reference) */}
                      <div className="tc-cal-left-panel">
                        <div className="tc-cal-month-nav-bar">
                          <span className="tc-cal-month-heading">{monthTitle}</span>
                          <div className="tc-cal-nav-buttons">
                            <button
                              type="button"
                              className="tc-cal-arrow-btn"
                              title="Previous Month"
                              onClick={handlePrevMonth}
                            >
                              <ChevronLeft size={16} />
                            </button>
                            <button
                              type="button"
                              className="tc-cal-arrow-btn"
                              title="Next Month"
                              onClick={handleNextMonth}
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="tc-cal-weekdays-row">
                          {WEEKDAYS_CAL.map((w) => (
                            <div key={w} className="tc-cal-weekday-label">{w}</div>
                          ))}
                        </div>

                        <div className="tc-cal-grid">
                          {/* Blank cells before 1st day of month */}
                          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                            <div key={`cal-empty-${i}`} className="tc-cal-cell empty" />
                          ))}

                          {/* Day numbers */}
                          {Array.from({ length: daysInMonth }).map((_, i) => {
                            const dayNum = i + 1;
                            const dateStr = `${calYear}-${String(calMonthIdx + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                            const daySlots = slotsByDate[dateStr] || [];
                            const hasSlots = daySlots.length > 0;
                            const isSelected = selectedDate === dateStr;

                            if (!hasSlots) {
                              return (
                                <div key={dateStr} className="tc-cal-cell no-slots">
                                  <span>{dayNum}</span>
                                </div>
                              );
                            }

                            return (
                              <button
                                key={dateStr}
                                type="button"
                                className={`tc-cal-cell available ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedDate(dateStr);
                                  setSelectedSlotId(null);
                                }}
                              >
                                <span className="tc-cal-day-number">{dayNum}</span>
                                <span className="tc-cal-slot-dot-indicator" />
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Right Column: Time Slots for Selected Date (Image 2 reference) */}
                      <div className="tc-cal-right-panel">
                        <div className="tc-cal-slots-header">
                          <h4 className="tc-cal-selected-date-heading">
                            {selectedDate ? (
                              new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })
                            ) : (
                              'Select a date'
                            )}
                          </h4>
                          {currentDaySlots.length > 0 && (
                            <span className="tc-cal-slots-count-tag">
                              {currentDaySlots.length} {currentDaySlots.length === 1 ? 'slot' : 'slots'}
                            </span>
                          )}
                        </div>

                        <div className="tc-cal-slots-vertical-list">
                          {currentDaySlots.length === 0 ? (
                            <div className="tc-cal-slots-empty">
                              <p>No interview slots on this date. Click an available date on the calendar.</p>
                            </div>
                          ) : (
                            currentDaySlots.map((slot) => {
                              const spotsLeft = slot.available_spots !== undefined ? slot.available_spots : (slot.availableSpots || 0);
                              const isFull = spotsLeft <= 0;
                              const isSelected = selectedSlotId === slot.id;
                              const isCurrentBooked = bookedSlot?.id === slot.id;

                              return (
                                <button
                                  key={slot.id}
                                  type="button"
                                  disabled={isFull && !isCurrentBooked}
                                  className={`tc-cal-slot-button ${isSelected ? 'selected' : ''} ${
                                    isFull ? 'full' : ''
                                  } ${isCurrentBooked ? 'current-booked' : ''}`}
                                  onClick={() => setSelectedSlotId(slot.id)}
                                >
                                  <div className="tc-cal-slot-top-row">
                                    <span
                                      className={`tc-cal-slot-status-dot ${isFull ? 'dot-full' : 'dot-available'}`}
                                      aria-hidden="true"
                                    />
                                    <span className="tc-cal-slot-time-text">
                                      {slot.start_time} - {slot.end_time}
                                    </span>
                                  </div>
                                  <div className="tc-cal-slot-bottom-row">
                                    {isCurrentBooked ? (
                                      <span className="tc-cal-slot-booked-text">Your Current Slot</span>
                                    ) : isFull ? (
                                      <span className="tc-cal-slot-full-text">Fully Booked (0 spots left)</span>
                                    ) : (
                                      <span className="tc-cal-slot-spots-text">
                                        {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} available
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Bottom Action Bar */}
                    <div className="tc-cal-bottom-actions">
                      <button
                        type="button"
                        className="tc-btn tc-btn-secondary"
                        onClick={() => setActiveStep(1)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <ChevronLeft size={16} /> Back to Step 1
                      </button>

                      <button
                        type="button"
                        disabled={!selectedSlotId || bookingSlot}
                        className="tc-btn tc-btn-primary tc-btn-large"
                        onClick={handleBookSlot}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <CheckCircle size={18} />
                        {bookingSlot
                          ? 'Confirming Slot...'
                          : rescheduleMode
                          ? 'Confirm Rescheduled Slot'
                          : 'Confirm Interview Slot'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingCampOnboarding;
